import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
  ReactNode,
  ReactElement,
} from 'react';
import { Toast, Link, Stack, IconButton } from '@airbus/components-react';
import { Close } from '@airbus/icons/react';
import { createUseStyles } from 'react-jss';
import { nanoid } from 'nanoid';

const AUTO_HIDE_DURATION_MS = 6000;

const useStyles = createUseStyles({
  toastWrapper: {
    padding: '4px',
    display: 'block',
  },
  toastContainer: {
    position: 'relative',
  },
  closeBtn: {
    position: 'absolute',
    top: '.4rem',
    right: '.5rem',
    zIndex: 90900,
  },
  errormessages: {
    position: 'fixed',
    top: '4rem',
    right: '20px',
    backgroundColor: '#B0B0B019',
    width: 'auto',
    maxHeight: 'calc(100vh - 7.5rem)',
    overflowY: 'auto',
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    zIndex: 10000,
    scrollbarWidth: 'none',
    borderRadius: '4px',
  },
});

type Extend = {
  children?: string;
  severity: 'success' | 'warning' | 'error' | 'info';
  autoHide?: boolean;
  isCloseButtonHide?: boolean;
  id?: string;
  title?: string;
  linkAddress?: string;
  linkText?: string;
  stopAutoClose?: boolean;
  createdAt?: number;
};

type AlertBarProp = Extend | null;

type AlertContextType = {
  alertbar: AlertBarProp | AlertBarProp[];
  setAlertbar: (alert: Extend | Extend[]) => void;
  clearAlerts: () => void;
  messages: Extend[];
};

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlertBar = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlertBar must be used within AlertBarContextProvider');
  }
  return context;
};

const AlertMessage = ({ msg, onRemove }: { msg: Extend; onRemove: (id: string) => void }) => {
  const classes = useStyles();

  useEffect(() => {
    if (!msg.autoHide || msg.stopAutoClose) return;

    const now = Date.now();
    const createdAt = msg.createdAt ?? now;
    const elapsed = now - createdAt;
    const remaining = AUTO_HIDE_DURATION_MS - elapsed;

    if (remaining <= 0) {
      onRemove(msg.id!);
      return;
    }

    const timer = setTimeout(() => onRemove(msg.id!), remaining);
    return () => clearTimeout(timer);
  }, [msg, onRemove]);

  const handleCloseClick = () => onRemove(msg.id!);

  return (
    <div className={classes.toastWrapper} key={msg.id}>
      <div className={classes.toastContainer}>
        {!msg.isCloseButtonHide && (
          <div className={classes.closeBtn}>
            <IconButton variant='ghost' size='xsmall' onClick={handleCloseClick}>
              <Close />
            </IconButton>
          </div>
        )}
        <Toast
          id={msg.id}
          variant={msg.severity}
          title={msg.title}
          onClose={handleCloseClick}
          data-testid={`message-${msg.id}`}
        >
          {msg.children || msg.linkText
            ? [
                msg?.children?.length > 0 && msg?.children,
                msg?.linkAddress?.length > 0 && (
                  <div key={msg.id} style={{ marginBottom: '.5rem' }} />
                ),
                msg?.linkAddress && (
                  <Link
                    key={msg.id}
                    href={msg?.linkAddress}
                    target='_blank'
                    style={{ fontWeight: '800' }}
                  >
                    {msg?.linkText}
                  </Link>
                ),
              ]
            : null}
        </Toast>
      </div>
    </div>
  );
};

export const AlertBarContextProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [alertbar, setAlertbarState] = useState<AlertBarProp | AlertBarProp[]>(null);
  const classes = useStyles();

  const setAlertbar = useCallback((alert: Extend | Extend[]) => {
    const now = Date.now();
    const newAlerts = (Array.isArray(alert) ? alert : [alert]).map((item) => ({
      ...item,
      id: item.id || nanoid(),
      autoHide: item.autoHide ?? true,
      createdAt: item.createdAt ?? now,
    }));

    setAlertbarState((prev) => {
      if (!prev) return newAlerts;
      const prevArray = Array.isArray(prev) ? prev : [prev];
      return [...prevArray, ...newAlerts];
    });
  }, []);

  const clearAlerts = useCallback(() => setAlertbarState(null), []);

  const handleRemove = useCallback((id: string) => {
    setAlertbarState((prev) => {
      if (!prev) return null;
      if (Array.isArray(prev)) {
        const filtered = prev.filter((m) => m?.id !== id);
        return filtered.length > 0 ? filtered : null;
      }
      if ((prev as Extend)?.id === id) {
        return null;
      }
      return prev;
    });
  }, []);

  const messages = useMemo(() => {
    if (!alertbar) return [];
    return Array.isArray(alertbar) ? alertbar : [alertbar];
  }, [alertbar]);

  return (
    <AlertContext.Provider value={{ alertbar, setAlertbar, clearAlerts, messages }}>
      {children}
      {messages.length > 0 && (
        <Stack spacing='1-x' className={classes.errormessages}>
          {messages.map((msg) => (
            <AlertMessage key={msg.id} msg={msg} onRemove={handleRemove} />
          ))}
        </Stack>
      )}
    </AlertContext.Provider>
  );
};